import { CatmullRomCurve3 } from 'three';
import { dampAngle } from '../../../utils/math.js';
import { MEDIAN_OPENINGS } from './route.js';
import { BUS_DIMENSIONS, createBusFleet } from './bus.js';

const BODY_COLORS = [
  0xece9e0, 0xd8d4c8, 0xf1eee6, 0xc9c6ba, 0xbeb7a6, 0xcfd2d0,
  0x8e9a9d, 0x394650, 0x6e2f2a, 0x4f684b, 0x8e7a55, 0xd0a931,
  0x2b3b55, 0x7f7d75, 0x6b7f8b, 0xa34c38,
];

const TYPE_WEIGHTS = [
  ['sedan', 0.64],
  ['wagon', 0.2],
  ['pickup', 0.16],
];

const LOOP_TOP_SPEED = 9.5;
const LOOP_MIN_SPEED = 4.0;
const LOOP_JOIN_SPEED = 4.2;
const LOOP_FOLLOW_GAP = 7.0;

// The street queue used to crawl at walking pace, which made waiting for a
// pump feel static. Keep it clearly a queue, but let it close gaps briskly and
// speed up the forecourt cycle so the line visibly advances.
const QUEUE_CREEP_SPEED = 3.6;
const QUEUE_FOLLOW_RATE = 1.5;
const SERVICE_SHIFT_SPEED = 8.0;
const ENTRY_CRUISE_SPEED = 7.0;
const ENTRY_MIN_DURATION = 3.0;
const AI_PUMP_TIME_MIN = 5;
const AI_PUMP_TIME_SPREAD = 5;

const RAKSHA_DEFAULTS = {
  speedMultiplier: 1.22,
  accelerationMultiplier: 1.4,
  followGap: 5.2,
  weaveAmplitude: 0.42,
  burnPerKm: 0.16,
  refuelRate: 0.18,
  lowFuelThreshold: 0.22,
};

// A queued Hilux becomes the car-following leader for the cars behind it. The
// centre gap leaves the truck's box clear instead of letting a scripted queue
// car drive into it.
const QUEUE_PLAYER_BUFFER = 0.9;

// The military technicals ignore the queue. Each station gets one open-backed
// pickup that patrols fast, cuts straight into the forecourt, refuels at a
// clear bay and then repeats the loop. Their road speed is four times the
// civilian top speed; curvature and the final stop still slow them down.
const MILITARY_SPEED_MULTIPLIER = 4;
const MILITARY_TOP_SPEED = LOOP_TOP_SPEED * MILITARY_SPEED_MULTIPLIER;
const MILITARY_ACCEL = 18;
const MILITARY_BRAKE = 24;
const MILITARY_REFUEL_TIME = 5.5;
// The swerve through the queue is still four times a civilian's pace, but slow
// enough that a clipped car is shoved aside instead of punted across the road.
const MILITARY_WEAVE_SPEED = 17;
const MILITARY_BUMP_DAMP = 3.2;
const MILITARY_BUMP_LIMIT = 3.4;

// Queue etiquette. The player has no formal queue slot, so cutting into the
// queue lane or pulling into a forecourt ahead of the waiting cars is treated
// as a jump. Nearby drivers swerve; while the player stays at the station, a
// fixed group of cars keeps circling and ramming them until they leave.
const RAGE_COOLDOWN = 24;
const RAGE_WINDOW = 10;
const RAGE_LOCAL_INTERVAL = 0.45;
const RAGE_LOCAL_RADIUS = 15;
const RAGE_LUNGE_DURATION = 1.3;
const RAGE_RUN_SPEED = 8.0;
const RAGE_RETURN_SPEED = 9.5;
const RAGE_RUN_TIMEOUT = 7.0;
const RAGE_SIEGE_TIMEOUT = 12.0;
// A skipped station stays hostile for the whole visit. The same four drivers
// attack continuously; nobody is swapped out or sent back to the queue early.
const STATION_RAGE_CARS = 4;
const STATION_LEAVE_GRACE = 3.0;
const STATION_ANGRY_CROWD = 14;
const STATION_BUMP_STRENGTH = 0.8;
const STATION_ATTACK_COOLDOWN = 2.0;
const STATION_RECOVER_TIME = 1.25;
const STATION_ATTACK_RADIUS = 5.05;
const STATION_RECOVER_DISTANCE = 8.0;
// One horn at irregular human intervals, not a synchronized chorus.
const STATION_HONK_MIN_INTERVAL = 1.3;
const STATION_HONK_MAX_INTERVAL = 3.4;

function nextStationHonkDelay() {
  return (
    STATION_HONK_MIN_INTERVAL +
    Math.random() * (STATION_HONK_MAX_INTERVAL - STATION_HONK_MIN_INTERVAL)
  );
}
// Four different approach points stop the pack collapsing onto one spot.
// Their radius stays just inside RAGE_BUMP_DISTANCE so a chasing car stops at
// the contact point instead of driving through the player's collider.
const STATION_APPROACH_OFFSETS = [
  { x: -STATION_ATTACK_RADIUS, z: 0 },
  { x: STATION_ATTACK_RADIUS, z: 0 },
  { x: 0, z: -STATION_ATTACK_RADIUS },
  { x: 0, z: STATION_ATTACK_RADIUS },
];
// Join behind the painted back of the queue and travel up its file to earn a
// clean arrival. Leaving the file for too long cancels that credit.
const QUEUE_JOIN_MARGIN = 10;
const QUEUE_FOLLOW_GRACE = 2.5;
const QUEUE_LEGIT_FRONT_REACH = 22;
// The AI queue has no player slot, so a driver who spends long enough actually
// waiting in the file earns the same clean arrival as one who reaches the
// front. Speeding through the file does not count.
const QUEUE_WAIT_CREDIT = 15;
const QUEUE_WAIT_MAX_SPEED = 6;

/**
 * The painted fuel queue in front of each station.
 *
 * Two files of cars stretch back from the stop line for 300 m. This is "the
 * gas line" — the longest queue in Khartoum — and the crows perch on the
 * forecourt trees along it, so the same numbers define both the traffic queue
 * and the audio trigger zone.
 */
export const STATION_QUEUE = {
  /** Metres behind the station where the back of the queue sits. */
  tail: 300,
  /** Metres before the station where the stop line is painted. */
  head: 34,
  /** Lateral offset of each queue file (positive = east of the centreline). */
  lineOffsets: [7.6, 9.84],
};
// Stop just outside the truck's box and deliver the hit as an impulse. The
// chasing body is kinematic, so letting it overlap at 7.4 m/s can eject the
// player at more than twice the car's speed.
const RAGE_BUMP_DISTANCE = 5.2;
// Small skin around the oriented boxes used to separate cars during a chase.
const RAGE_COLLISION_MARGIN = 0.15;
// Contact skin for player-vs-traffic damage. This is deliberately smaller
// than the separation margin: cars must actually touch the Hilux, not merely
// drive alongside it.
const PLAYER_COLLISION_MARGIN = 0.06;
const PLAYER_BUMP_IMPULSE = 1100;

export function carDimensions(type) {
  if (type === 'bus') return BUS_DIMENSIONS;
  if (type === 'raksha') {
    return { width: 1.5, height: 2.02, length: 2.7, wheelZ: 0.9, lightZ: 1.24 };
  }
  if (type === 'military') {
    return { width: 2.02, height: 1.8, length: 5.0, wheelZ: 1.72, lightZ: 2.5 };
  }
  if (type === 'pickup') return { width: 1.98, height: 1.7, length: 4.65, wheelZ: 1.5, lightZ: 2.26 };
  if (type === 'wagon') return { width: 1.94, height: 1.58, length: 4.5, wheelZ: 1.45, lightZ: 2.2 };
  return { width: 1.94, height: 1.56, length: 4.42, wheelZ: 1.4, lightZ: 2.16 };
}

/**
 * The queue is a live, lightweight simulation rather than a static row.
 *
 * Each station has two queue files on the street. The front car of a file
 * waits at the stop line, then drives through the south forecourt entrance
 * into a three-car service lane beside the pumps. There it waits, moves up to
 * the pump, fuels, and finally exits north through the other driveway back
 * onto the road. Instead of vanishing, it then drives to a median opening,
 * U-turns onto the southbound carriageway, comes back past the station, makes
 * a second U-turn and rejoins the back of its queue. Cars behind creep forward
 * in a car-following queue.
 */
export function createTrafficFlow({
  route,
  stations,
  random,
  hitchhikers = null,
  rakshaConfig = null,
  busConfig = null,
}) {
  const records = [];
  const loops = [];
  const rakshaSettings = { ...RAKSHA_DEFAULTS, ...(rakshaConfig ?? {}) };

  const makeCar = (x, z, yaw, options = {}) => {
    const type = options.type ?? pickType(random());
    const record = {
      index: records.length,
      x,
      z,
      y: 0.02,
      yaw,
      scale: options.scale ?? random.range(0.95, 1.045),
      color: options.color ?? random.pick(BODY_COLORS),
      type,
      hazard: type === 'raksha' ? false : (options.hazard ?? true),
      hazardOn: type === 'raksha' ? false : (options.hazard ?? true),
      visualScale: 1,
      visible: true,
      state: 'queue',
      angry: false,
      angryTimer: 0,
      angerCooldown: 0,
      lunge: null,
      rage: null,
      raging: false,
      bumpX: 0,
      bumpZ: 0,
      bumpYaw: 0,
    };
    if (type === 'raksha') {
      record.scale = options.scale ?? 1;
      record.fuel = options.fuel ?? random.range(0.28, 0.9);
      record.rakshaConfig = rakshaSettings;
      record.raksha = {
        passengers: [],
        stop: null,
        target: null,
        stopTimer: 0,
        cooldown: random.range(0, 5),
        dropStarted: false,
      };
      record._fuelX = x;
      record._fuelZ = z;
    }
    records.push(record);
    return record;
  };

  for (let stationIndex = 0; stationIndex < stations.length; stationIndex += 1) {
    loops.push(createStationLoop({
      station: stations[stationIndex],
      stationIndex,
      route,
      random,
      makeCar,
    }));
  }

  // The buses are public transport, not queuers: each one owns a closed circuit
  // between two median openings and never touches a pump or a queue slot.
  const busFleet = createBusFleet({
    route,
    random,
    makeCar,
    queueSpans: stations.map((station) => ({
      start: station.s - STATION_QUEUE.tail,
      end: station.s - STATION_QUEUE.head,
    })),
    config: busConfig,
  });

  return {
    records,
    loops,
    buses: busFleet.buses,
    // The drone raid raises this briefly, making drivers accelerate and close
    // gaps. It is intentionally a single multiplier so normal traffic logic
    // still owns gaps, stops and joining safely.
    panicFactor: 1,
    update(dt, player) {
      let event = null;
      const panic = Math.max(1, this.panicFactor ?? 1);
      hitchhikers?.beginRideUpdate();
      for (const car of records) {
        car._queueYield = false;
        decayTrafficBump(car, dt);
      }
      for (const loop of loops) {
        const loopEvent = updateLoop(loop, dt, route, player, panic, hitchhikers);
        if (loopEvent && !event) event = loopEvent;
      }
      busFleet.update(dt, panic, hitchhikers);
      // Military impacts are visual shoves, not another simulation layer. Add
      // the live knock offset after every scripted car has been placed, then
      // let it decay over the next second.
      applyTrafficBumps(records);
      resolveMilitaryBumps(records);
      resolveRageCollisions(records, player?.mesh?.position);
      detectPlayerCarCollisions(records, player);
      updateRakshaFuel(records);
      return event;
    },
  };
}

function updateRakshaFuel(records) {
  for (const car of records) {
    if (car.type !== 'raksha') continue;
    const moved = Math.hypot(car.x - car._fuelX, car.z - car._fuelZ);
    // Ignore teleports or a military shove; only actual road distance burns fuel.
    if (moved < 2 && car.state !== 'service') {
      car.fuel = Math.max(0, car.fuel - (moved / 1000) * car.rakshaConfig.burnPerKm);
    }
    car._fuelX = car.x;
    car._fuelZ = car.z;
  }
}

function collisionBox(car, margin = RAGE_COLLISION_MARGIN) {
  const dimensions = carDimensions(car.type);
  const scale = car.scale ?? 1;
  const cos = Math.cos(car.yaw);
  const sin = Math.sin(car.yaw);
  return {
    x: car.x,
    z: car.z,
    halfWidth: (dimensions.width * 0.5) * scale + margin,
    halfLength: (dimensions.length * 0.5) * scale + margin,
    rightX: cos,
    rightZ: -sin,
    forwardX: -sin,
    forwardZ: -cos,
  };
}

function boxesOverlap(a, b) {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const axes = [
    a.rightX, a.rightZ,
    a.forwardX, a.forwardZ,
    b.rightX, b.rightZ,
    b.forwardX, b.forwardZ,
  ];

  let depth = Infinity;
  let normalX = 0;
  let normalZ = 0;

  for (let i = 0; i < axes.length; i += 2) {
    const axisX = axes[i];
    const axisZ = axes[i + 1];
    const distance = Math.abs(dx * axisX + dz * axisZ);
    const radiusA =
      a.halfWidth * Math.abs(a.rightX * axisX + a.rightZ * axisZ) +
      a.halfLength * Math.abs(a.forwardX * axisX + a.forwardZ * axisZ);
    const radiusB =
      b.halfWidth * Math.abs(b.rightX * axisX + b.rightZ * axisZ) +
      b.halfLength * Math.abs(b.forwardX * axisX + b.forwardZ * axisZ);
    const overlap = radiusA + radiusB - distance;
    if (overlap <= 0) return null;
    if (overlap < depth) {
      depth = overlap;
      const sign = dx * axisX + dz * axisZ >= 0 ? 1 : -1;
      normalX = axisX * sign;
      normalZ = axisZ * sign;
    }
  }

  return { depth, normalX, normalZ };
}

function playerCollisionBox(player) {
  const mesh = player?.mesh;
  if (!mesh) return null;

  const chassis = player.config?.chassis ?? [1.7, 0.6, 4.4];
  const yaw = Number.isFinite(player.heading) ? player.heading : 0;
  const cos = Math.cos(yaw);
  const sin = Math.sin(yaw);

  return {
    x: mesh.position.x,
    z: mesh.position.z,
    // Player model faces local +Z, unlike the traffic cars' local -Z nose.
    halfWidth: chassis[0] * 0.5 + PLAYER_COLLISION_MARGIN,
    halfLength: chassis[2] * 0.5 + PLAYER_COLLISION_MARGIN,
    rightX: cos,
    rightZ: -sin,
    forwardX: sin,
    forwardZ: cos,
  };
}

/**
 * Turn physical car-to-car contact into one damage event per contact episode.
 * The damage handler owns a short per-car cooldown, which also prevents a
 * scripted rage bump and its physical contact from counting twice.
 */
function detectPlayerCarCollisions(records, player) {
  const playerBox = playerCollisionBox(player);
  if (!playerBox) {
    for (const car of records) car._playerTouching = false;
    return;
  }

  for (const car of records) {
    const carBox = collisionBox(car, PLAYER_COLLISION_MARGIN);
    const dx = carBox.x - playerBox.x;
    const dz = carBox.z - playerBox.z;
    // Use the full half-diagonal here: a rotated long car can overlap while
    // its centre is farther out than its half-length on one axis.
    const reachA = Math.hypot(playerBox.halfWidth, playerBox.halfLength);
    const reachB = Math.hypot(carBox.halfWidth, carBox.halfLength);
    const broadphase = reachA + reachB + 0.1;
    const touching =
      Math.abs(dx) <= broadphase &&
      Math.abs(dz) <= broadphase &&
      boxesOverlap(playerBox, carBox) !== null;

    if (touching && !car._playerTouching) {
      // Cars yielding to a player who is genuinely following the queue must
      // never count as impacts. Their hold distance should keep the boxes
      // apart, but this also stops a collider sync edge case from costing
      // health or knocking the Hilux out of the line.
      if (!car._queueYield) {
        player.collideWithCar?.(car, { source: 'impact' });
      }
    }
    car._playerTouching = touching;
  }
}

/**
 * Traffic movement is kinematic, so pairs can otherwise pass through one
 * another during a rage run. Resolve the overlap as an oriented-box collision:
 * two chasers share the correction, while queued or parked cars push the
 * moving chaser clear without being knocked out of their own slots.
 */
function resolveRageCollisions(records, playerPosition) {
  const ragers = records.filter((car) => car.rage);
  if (ragers.length === 0) return;

  // Keep a car-car correction from shoving a chaser into the player's body.
  // The normal attack circle already sits outside the truck; only corrections
  // caused by another traffic car need this radial guard.
  const keepClearOfPlayer = (car) => {
    if (!playerPosition) return;
    const dx = car.x - playerPosition.x;
    const dz = car.z - playerPosition.z;
    const distance = Math.hypot(dx, dz);
    if (distance < 1e-4 || distance >= RAGE_BUMP_DISTANCE) return;
    car.x = playerPosition.x + (dx / distance) * RAGE_BUMP_DISTANCE;
    car.z = playerPosition.z + (dz / distance) * RAGE_BUMP_DISTANCE;
  };

  for (let pass = 0; pass < 5; pass += 1) {
    for (const car of ragers) {
      for (const other of records) {
        if (other === car) continue;
        // Both cars are handled from the lower index so a pair only gets one
        // shared correction per pass.
        if (other.rage && other.index < car.index) continue;

        const a = collisionBox(car);
        const b = collisionBox(other);
        const dx = b.x - a.x;
        const dz = b.z - a.z;
        const broadphase = a.halfLength + b.halfLength + 0.1;
        if (Math.abs(dx) > broadphase || Math.abs(dz) > broadphase) continue;

        const hit = boxesOverlap(a, b);
        if (!hit) continue;

        const move = hit.depth + 0.015;
        if (other.rage) {
          car.x -= hit.normalX * move * 0.5;
          car.z -= hit.normalZ * move * 0.5;
          other.x += hit.normalX * move * 0.5;
          other.z += hit.normalZ * move * 0.5;
          keepClearOfPlayer(other);
        } else {
          car.x -= hit.normalX * move;
          car.z -= hit.normalZ * move;
        }
        keepClearOfPlayer(car);
      }
    }
  }
}

function decayTrafficBump(car, dt) {
  if (!car.bumpX && !car.bumpZ && !car.bumpYaw) return;
  const damping = Math.exp(-MILITARY_BUMP_DAMP * dt);
  car.bumpX *= damping;
  car.bumpZ *= damping;
  car.bumpYaw *= damping;
  if (Math.abs(car.bumpX) < 0.003) car.bumpX = 0;
  if (Math.abs(car.bumpZ) < 0.003) car.bumpZ = 0;
  if (Math.abs(car.bumpYaw) < 0.002) car.bumpYaw = 0;
}

function applyTrafficBumps(records) {
  for (const car of records) {
    if (!car.bumpX && !car.bumpZ && !car.bumpYaw) continue;
    car.x += car.bumpX;
    car.z += car.bumpZ;
    car.yaw += car.bumpYaw;
  }
}

/**
 * The military pickup is a kinematic scripted vehicle, so civilian traffic
 * cannot physically react to it through Bullet. Resolve those contacts as a
 * temporary knock offset: the civilian is shoved out of the military's path,
 * then blends back into its own queue, pump or return-loop slot.
 */
function resolveMilitaryBumps(records) {
  const militaryCars = records.filter((car) => car.type === 'military');
  if (militaryCars.length === 0) return;

  for (const military of militaryCars) {
    if (military.visible === false) continue;
    const a = collisionBox(military, 0.1);
    for (const car of records) {
      // A bus is on rails like the technical itself: a shove would only be
      // overwritten by its own curve next frame, so skip it rather than let it
      // jitter for one frame.
      if (car === military || car.type === 'military' || car.railed || car.visible === false) continue;

      const b = collisionBox(car, 0.1);
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const broadphase = a.halfLength + b.halfLength + 0.1;
      if (Math.abs(dx) > broadphase || Math.abs(dz) > broadphase) continue;

      const hit = boxesOverlap(a, b);
      if (!hit) continue;

      const current = Math.hypot(car.bumpX, car.bumpZ);
      const room = MILITARY_BUMP_LIMIT - current;
      if (room <= 0.01) continue;

      const push = Math.min(hit.depth + 0.22, 0.8, room);
      const side =
        (car.x - military.x) * a.rightX + (car.z - military.z) * a.rightZ;
      const spin = Math.sign(side || 1) * Math.min(0.22, push * 0.08);

      car.bumpX += hit.normalX * push;
      car.bumpZ += hit.normalZ * push;
      car.bumpYaw = Math.max(-0.38, Math.min(0.38, car.bumpYaw + spin));
      car.x += hit.normalX * push;
      car.z += hit.normalZ * push;
      car.yaw += spin;
    }
  }
}

function createStationLoop({ station, stationIndex, route, random, makeCar }) {
  const frame = station.frame;
  const lineStart = station.s - STATION_QUEUE.tail;
  const lineEnd = station.s - STATION_QUEUE.head;
  const lineOffsets = STATION_QUEUE.lineOffsets;
  const lineCounts = [40, 36];
  const serviceX = [26.2, 31.8];
  const targetGap = [6.25, 6.95];
  const slotsZ = [-10, 0, 10];

  // Leave via the north driveway, U-turn at the first median gap north of the
  // station, come back south, U-turn again beyond the queue, and rejoin the
  // queue from the south. The two stations use disjoint road sections, so
  // their returning cars never meet on the carriageway.
  const joinS = lineStart - 40;
  const northOpening = MEDIAN_OPENINGS.find((opening) => opening.s > station.s + 100);
  const southOpening = [...MEDIAN_OPENINGS]
    .reverse()
    .find((opening) => opening.s < lineStart - 20);

  const toWorld = (x, z) => frame.position
    .clone()
    .addScaledVector(frame.right, x)
    .addScaledVector(frame.tangent, -z);

  const loop = {
    station,
    stationIndex,
    frame,
    lines: [],
    entryBusy: null,
    exitBusy: false,
    exiting: [],
    nextEntryLane: 0,
    joinS,
    northOpening,
    southOpening,
    rageCooldown: 0,
    rageWindow: 0,
    localRageCooldown: 0,
    playerInRageZone: false,
    // Persistent station anger. Once a queue-jumper is inside the forecourt,
    // the station remains angry until they leave or prove they queued.
    stationRageActive: false,
    stationRageAnnounced: false,
    stationOutside: 0,
    stationSiegeStarted: false,
    stationChaseCars: [],
    stationHonkCooldown: 0,
    honkPulse: 0,
    // Queue-following credit for a fair arrival at the pumps.
    queueFollowing: false,
    queueFollowMaxS: -Infinity,
    queueFollowLost: 0,
    queueWaitTime: 0,
    queueCreditEarned: false,
    queueLegitimate: false,
    // When the player is genuinely following a file, this holds the projected
    // route slot so the car-following update can leave a gap behind the truck.
    queueGuard: null,
  };

  for (let lineIndex = 0; lineIndex < 2; lineIndex += 1) {
    const count = lineCounts[lineIndex];
    const spacing = (lineEnd - lineStart) / (count - 1);
    const line = {
      index: lineIndex,
      offset: lineOffsets[lineIndex],
      targetGap: targetGap[lineIndex],
      lineStart,
      lineEnd,
      cars: [],
      lane: {
        index: lineIndex,
        x: serviceX[lineIndex],
        slots: slotsZ.map((z) => ({ z, car: null })),
        entryCurve: null,
        entryStop: 0,
        exitCurve: null,
      },
    };

    for (let i = 0; i < count; i += 1) {
      const s = lineEnd - i * spacing;
      const lateral = line.offset + random.range(-0.04, 0.04);
      const carFrame = route.offsetFrame(s, lateral);
      const car = makeCar(carFrame.position.x, carFrame.position.z, carFrame.yaw, {
        type: i % 12 === 4 ? 'raksha' : undefined,
        hazard: random() < 0.82,
      });
      car.state = 'queue';
      car.queueS = s;
      car.queueLateral = lateral;
      car.yawOffset = random.range(-0.025, 0.025);
      car.joining = false;
      car.homeLine = lineIndex;
      car.stationIndex = stationIndex;
      line.cars.push(car);
    }

    line.lane.entryCurve = buildEntryCurve({
      route,
      lineEnd,
      lineOffset: line.offset,
      serviceX: line.lane.x,
      toWorld,
    });
    line.lane.entryStop = Math.max(2, line.lane.entryCurve.getLength() - 6);
    line.lane.exitCurve = buildExitCurve({
      route,
      station,
      serviceX: line.lane.x,
      lineOffset: line.offset,
      northOpening,
      southOpening,
      joinS,
      toWorld,
    });
    line.lane.exitLength = line.lane.exitCurve.getLength();
    line.lane.speedProfile = buildSpeedProfile(line.lane.exitCurve, line.lane.exitLength);

    // Three cars are already inside each service lane at start-up.
    for (let slotIndex = 0; slotIndex < slotsZ.length; slotIndex += 1) {
      const slot = line.lane.slots[slotIndex];
      const point = toWorld(line.lane.x, slot.z);
      const car = makeCar(point.x, point.z, frame.yaw, {
        type: slotIndex === 1 ? 'raksha' : undefined,
        hazard: false,
      });
      car.state = 'service';
      car.stationIndex = stationIndex;
      car.homeLine = lineIndex;
      car.serviceLane = line.lane;
      car.slotIndex = slotIndex;
      car.localZ = slot.z;
      car.fuelRemaining = slotIndex === 0 ? random.range(3.5, 6.5) : null;
      slot.car = car;
    }

    loop.lines.push(line);
  }

  loop.military = createMilitaryPatrol({
    loop,
    route,
    random,
    makeCar,
    toWorld,
  });

  return loop;
}

/**
 * A single military technical belongs to each station. It uses its own closed
 * circuit instead of a queue slot: out through the north driveway, around the
 * two median U-turns, back north past the queue, then straight into the south
 * driveway and onto a clear refuel bay beside the pumps. It never joins a
 * queue, so it is naturally exempt from the queue-rage system.
 */
function createMilitaryPatrol({ loop, route, random, makeCar, toWorld }) {
  const { curve, weave } = buildMilitaryCurve({
    route,
    station: loop.station,
    northOpening: loop.northOpening,
    southOpening: loop.southOpening,
    toWorld,
  });
  const length = curve.getLength();
  const speedProfile = buildMilitarySpeedProfile(curve, length, weave);
  const start = curve.getPointAt(0);
  const tangent = curve.getTangentAt(0);
  const car = makeCar(
    start.x,
    start.z,
    Math.atan2(-tangent.x, -tangent.z),
    {
      type: 'military',
      hazard: false,
      scale: 1,
      color: 0x5f6847,
    },
  );
  car.state = 'military';
  car.stationIndex = loop.stationIndex;
  car.homeLine = -1;
  car.militaryRole = true;

  const patrol = {
    record: car,
    curve,
    weave,
    length,
    speedProfile,
    distance: 0,
    speed: 0,
    state: 'refueling',
    timer: random.range(2.0, 4.8),
    refuels: 0,
    maxSpeed: 0,
    distanceTravelled: 0,
  };
  placeMilitaryPatrol(patrol, 0);
  return patrol;
}

function buildMilitaryCurve({
  route,
  station,
  northOpening,
  southOpening,
  toWorld,
}) {
  const northTurnS = northOpening.s - 4;
  const northApexS = northTurnS + 4.5;
  const southTurnS = southOpening.s + 4;
  const southApexS = southTurnS - 4.5;

  const points = [
    // Stop under the canopy edge, west of the pump islands. This keeps the
    // military bay clear of the three scripted service-lane positions.
    toWorld(23.2, -5.0),
    toWorld(23.2, -9.0),
    toWorld(22.8, -13.2),
    toWorld(18.5, -15.5),
    toWorld(13.8, -16.0),
    route.offsetFrame(station.s + 18, 7.6).position,
    route.offsetFrame(station.s + 26, 6.2).position,
    route.offsetFrame(station.s + 40, 4.8).position,
    route.offsetFrame(station.s + 52, 4.5).position,
  ];

  pushRouteLeg(points, route, station.s + 52, northTurnS - 20, 4.5);
  points.push(
    route.offsetFrame(northTurnS, 4.5).position,
    route.offsetFrame(northTurnS + 3.18, 3.18).position,
    route.offsetFrame(northApexS, 0).position,
    route.offsetFrame(northTurnS + 3.18, -3.18).position,
    route.offsetFrame(northTurnS, -4.5).position,
  );

  pushRouteLeg(points, route, northTurnS, southTurnS + 20, -4.5);
  points.push(
    route.offsetFrame(southTurnS, -4.5).position,
    route.offsetFrame(southTurnS - 3.18, -3.18).position,
    route.offsetFrame(southApexS, 0).position,
    route.offsetFrame(southTurnS - 3.18, 3.18).position,
    route.offsetFrame(southTurnS, 4.5).position,
  );

  // North along the inner lane, then the last stretch beside the queue is a
  // deliberate weave: the technical swerves into the waiting file, clips the
  // cars and swings back out. Nobody in the queue reacts, so each shove is a
  // one-way event that decays back into the car's own slot.
  pushRouteLeg(points, route, southTurnS, station.s - 156, 4.5);
  const weaveStart = route.offsetFrame(station.s - 150, 4.5).position;
  const weaveEnd = route.offsetFrame(station.s - 40, 4.5).position;
  points.push(weaveStart);
  for (const [offset, lateral] of [
    [138, 6.95],
    [124, 4.5],
    [110, 6.95],
    [96, 4.5],
    [82, 6.95],
    [66, 4.5],
  ]) {
    points.push(route.offsetFrame(station.s - offset, lateral).position);
  }
  points.push(weaveEnd);

  // East through the south driveway to the refuel bay. No slot in either queue
  // is consumed.
  points.push(
    route.offsetFrame(station.s - 26, 4.5).position,
    route.offsetFrame(station.s - 20, 5.2).position,
    route.offsetFrame(station.s - 13, 7.0).position,
    toWorld(13.5, 16.0),
    toWorld(16.5, 13.5),
    toWorld(20.6, 9.0),
    toWorld(22.8, 3.0),
    toWorld(23.2, -1.0),
  );

  const curve = new CatmullRomCurve3(points, true, 'centripetal', 0.5);
  return {
    curve,
    weave: {
      startU: findCurveU(curve, weaveStart),
      endU: findCurveU(curve, weaveEnd),
    },
  };
}

/** Arc-length position of the sample nearest a known waypoint. */
function findCurveU(curve, target) {
  let bestU = 0;
  let bestDistance = Infinity;
  for (let i = 0; i <= 400; i += 1) {
    const u = i / 400;
    const distance = curve.getPointAt(u).distanceTo(target);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestU = u;
    }
  }
  return bestU;
}

function buildMilitarySpeedProfile(curve, length, weave) {
  const count = Math.max(100, Math.min(800, Math.ceil(length / 2)));
  const step = length / count;
  const limits = new Float32Array(count + 1);

  let previous = curve.getTangentAt(0);
  for (let i = 1; i <= count; i += 1) {
    const tangent = curve.getTangentAt(i / count);
    const angle = previous.angleTo(tangent);
    const curvature = angle / step;
    const limit = curvature > 1e-5
      ? Math.sqrt(2.6 / curvature)
      : LOOP_TOP_SPEED;
    limits[i] = Math.min(
      MILITARY_TOP_SPEED,
      Math.max(LOOP_MIN_SPEED, Math.min(LOOP_TOP_SPEED, limit))
        * MILITARY_SPEED_MULTIPLIER,
    );
    previous = tangent;
  }
  limits[0] = limits[1];

  for (let pass = 0; pass < 2; pass += 1) {
    const copy = limits.slice();
    for (let i = 1; i < count; i += 1) {
      limits[i] = (copy[i - 1] + 2 * copy[i] + copy[i + 1]) * 0.25;
    }
  }

  // The truck brakes hard for its bay instead of driving through the pumps.
  const slowSamples = Math.ceil(24 / step);
  for (let i = 0; i < Math.min(count, slowSamples); i += 1) {
    limits[i] = Math.min(limits[i], 7.0);
  }
  for (let i = Math.max(0, count - slowSamples); i <= count; i += 1) {
    limits[i] = Math.min(limits[i], 7.0);
  }

  // The queue weave runs at a capped speed so the shove stays a shove.
  if (weave) {
    const from = Math.max(0, Math.floor(weave.startU * count));
    const to = Math.min(count, Math.ceil(weave.endU * count));
    for (let i = from; i <= to; i += 1) {
      limits[i] = Math.min(limits[i], MILITARY_WEAVE_SPEED);
    }
  }

  const braking = 9;
  for (let i = count - 1; i >= 0; i -= 1) {
    limits[i] = Math.min(
      limits[i],
      Math.sqrt(limits[i + 1] * limits[i + 1] + 2 * braking * step),
    );
  }

  return { count, step, limits };
}

function updateMilitaryPatrol(loop, dt) {
  const patrol = loop.military;
  if (!patrol) return;
  const record = patrol.record;

  if (patrol.state === 'refueling') {
    patrol.timer -= dt;
    patrol.speed = 0;
    placeMilitaryPatrol(patrol, 0);
    if (patrol.timer <= 0) {
      patrol.state = 'road';
      patrol.distance = 0.25;
      patrol.speed = 1;
    }
    return;
  }

  const progress = patrol.distance / patrol.length;
  const target = sampleSpeedProfile(patrol.speedProfile, progress);
  if (patrol.speed < target) {
    patrol.speed = Math.min(target, patrol.speed + MILITARY_ACCEL * dt);
  } else {
    patrol.speed = Math.max(target, patrol.speed - MILITARY_BRAKE * dt);
  }

  const next = patrol.distance + patrol.speed * dt;
  patrol.maxSpeed = Math.max(patrol.maxSpeed, patrol.speed);
  patrol.distanceTravelled += patrol.speed * dt;

  if (next >= patrol.length) {
    patrol.distance = 0;
    patrol.speed = 0;
    patrol.state = 'refueling';
    patrol.timer = MILITARY_REFUEL_TIME;
    patrol.refuels += 1;
    placeMilitaryPatrol(patrol, 0);
    return;
  }

  patrol.distance = next;
  placeMilitaryPatrol(patrol, next / patrol.length);
  record.visible = true;
}

function placeMilitaryPatrol(patrol, u) {
  const record = patrol.record;
  const clamped = Math.max(0, Math.min(1, u));
  const point = patrol.curve.getPointAt(clamped);
  const tangent = patrol.curve.getTangentAt(clamped);
  record.x = point.x;
  record.z = point.z;
  record.y = 0.02;
  record.yaw = Math.atan2(-tangent.x, -tangent.z);
  record.visible = true;
  record.state = 'military';
  record.bumpX = 0;
  record.bumpZ = 0;
  record.bumpYaw = 0;
}

function buildEntryCurve({ route, lineEnd, lineOffset, serviceX, toWorld }) {
  const points = [];
  points.push(route.offsetFrame(lineEnd, lineOffset).position);
  points.push(route.offsetFrame(lineEnd + 7, lineOffset + 0.15).position);
  points.push(route.offsetFrame(lineEnd + 13, lineOffset + 1.6).position);
  points.push(toWorld(13.5, 18.0));
  points.push(toWorld(18.0, 16.6));
  points.push(toWorld(23.0, 15.2));
  points.push(toWorld(serviceX, 13.0));
  points.push(toWorld(serviceX, 10.0));
  points.push(toWorld(serviceX, 4.0));

  return new CatmullRomCurve3(points, false, 'centripetal', 0.5);
}

function buildExitCurve({
  route,
  station,
  serviceX,
  lineOffset,
  northOpening,
  southOpening,
  joinS,
  toWorld,
}) {
  // A U-turn uses the inner lanes either side of the median. The arc is
  // centred in the opening and only needs about nine metres of road length.
  const northTurnS = northOpening.s - 4;
  const northApexS = northTurnS + 4.5;
  const southTurnS = southOpening.s + 4;
  const southApexS = southTurnS - 4.5;

  const points = [
    toWorld(serviceX, -10),
    toWorld(serviceX, -17),
    toWorld(serviceX - 4, -20.5),
    toWorld(18, -20),
    toWorld(14, -18),
    route.offsetFrame(station.s + 20, 10.0).position,
    route.offsetFrame(station.s + 26, 8.2).position,
    route.offsetFrame(station.s + 38, 5.5).position,
    route.offsetFrame(station.s + 50, 4.5).position,
  ];

  // Northbound lane up to the northern U-turn.
  pushRouteLeg(points, route, station.s + 50, northTurnS - 20, 4.5);
  points.push(
    route.offsetFrame(northTurnS, 4.5).position,
    route.offsetFrame(northTurnS + 3.18, 3.18).position,
    route.offsetFrame(northApexS, 0).position,
    route.offsetFrame(northTurnS + 3.18, -3.18).position,
    route.offsetFrame(northTurnS, -4.5).position,
  );

  // Southbound carriageway, back past the station.
  pushRouteLeg(points, route, northTurnS, southTurnS + 20, -4.5);
  points.push(
    route.offsetFrame(southTurnS, -4.5).position,
    route.offsetFrame(southTurnS - 3.18, -3.18).position,
    route.offsetFrame(southApexS, 0).position,
    route.offsetFrame(southTurnS - 3.18, 3.18).position,
    route.offsetFrame(southTurnS, 4.5).position,
  );

  // Northbound again, then ease into this file's lane and rejoin the queue.
  pushRouteLeg(points, route, southTurnS, joinS - 80, 4.5);
  points.push(
    route.offsetFrame(joinS - 30, lineOffset).position,
    route.offsetFrame(joinS, lineOffset).position,
  );

  return new CatmullRomCurve3(points, false, 'centripetal', 0.5);
}

function pushRouteLeg(points, route, fromS, toS, lateral, spacing = 40) {
  const span = Math.abs(toS - fromS);
  const steps = Math.max(1, Math.ceil(span / spacing));
  for (let i = 1; i <= steps; i += 1) {
    const s = fromS + (toS - fromS) * (i / steps);
    points.push(route.offsetFrame(s, lateral).position);
  }
}

/**
 * Precompute a speed limit for every sample of the departure loop. The limit
 * comes from the local curvature and is then swept backwards so a car starts
 * braking before, rather than at, the U-turns. The final stretch is kept slow
 * so the hand-off into the queue is smooth.
 */
function buildSpeedProfile(curve, length) {
  const count = Math.max(80, Math.min(700, Math.ceil(length / 2)));
  const step = length / count;
  const limits = new Float32Array(count + 1);

  let previous = curve.getTangentAt(0);
  for (let i = 1; i <= count; i += 1) {
    const tangent = curve.getTangentAt(i / count);
    const angle = previous.angleTo(tangent);
    const curvature = angle / step;
    const limit = curvature > 1e-5
      ? Math.sqrt(2.6 / curvature)
      : LOOP_TOP_SPEED;
    limits[i] = Math.max(LOOP_MIN_SPEED, Math.min(LOOP_TOP_SPEED, limit));
    previous = tangent;
  }
  limits[0] = limits[1];

  // Two smoothing passes keep numeric noise at spline joins from causing
  // phantom slow zones, while leaving the tight U-turns clearly limited.
  for (let pass = 0; pass < 2; pass += 1) {
    const copy = limits.slice();
    for (let i = 1; i < count; i += 1) {
      limits[i] = (copy[i - 1] + 2 * copy[i] + copy[i + 1]) * 0.25;
    }
  }

  const slowSamples = Math.ceil(32 / step);
  for (let i = Math.max(0, count - slowSamples); i <= count; i += 1) {
    limits[i] = Math.min(limits[i], LOOP_JOIN_SPEED);
  }

  const braking = 2.0;
  for (let i = count - 1; i >= 0; i -= 1) {
    limits[i] = Math.min(
      limits[i],
      Math.sqrt(limits[i + 1] * limits[i + 1] + 2 * braking * step),
    );
  }

  return { count, step, limits };
}

function sampleSpeedProfile(profile, u) {
  const x = Math.max(0, Math.min(1, u)) * profile.count;
  const index = Math.min(profile.count - 1, Math.floor(x));
  const fraction = x - index;
  return profile.limits[index] * (1 - fraction) + profile.limits[index + 1] * fraction;
}

function updateLoop(loop, dt, route, player, panic = 1, hitchhikers = null) {
  loop.rageCooldown = Math.max(0, loop.rageCooldown - dt);
  loop.rageWindow = Math.max(0, loop.rageWindow - dt);
  loop.localRageCooldown = Math.max(0, loop.localRageCooldown - dt);
  const rageEvent = checkQueueRage(loop, player, route, dt);

  // As the player runs along the line, cars further down the queue take their
  // turn swerving at the Hilux. This makes a fast shoulder pass a rolling
  // argument instead of a single reaction at the entry point.
  if (
    loop.rageWindow > 0 &&
    loop.playerInRageZone &&
    loop.localRageCooldown <= 0 &&
    Math.abs(player?.speed ?? 0) > 1.1
  ) {
    provokeNearbyQueue(loop, player);
    loop.localRageCooldown = RAGE_LOCAL_INTERVAL;
  }

  updateStationRage(loop, dt, player);

  // Street queue first: cars creep forward into the gap left by the entry car.
  // A player following the file becomes the leader for the cars behind them.
  for (const line of loop.lines) {
    updateQueueLine(line, dt, route, player, loop.queueGuard, panic);
  }

  // Service lanes: move cars between pump positions and run the fuel timer.
  for (const line of loop.lines) updateServiceLane(loop, line, dt, panic);

  updateEntryCar(loop, dt);

  // Start at most one entry into the station per tick.
  if (!loop.entryBusy) {
    const first = loop.nextEntryLane;
    for (let i = 0; i < 2; i += 1) {
      const line = loop.lines[(first + i) % 2];
      if (line.lane.slots[2].car) continue;
      const front = line.cars[0];
      if (!front || front.raging) continue;
      if (front.queueS < line.lineEnd - 1.0) continue;
      startEntry(loop, line, front);
      loop.nextEntryLane = (line.index + 1) % 2;
      break;
    }
  }

  updateRageRuns(loop, dt, route, player);
  updateAngryTimers(loop, dt);

  // Departing cars keep driving the loop even while other lanes work.
  updateExiting(loop, dt, panic, route, hitchhikers);

  // The station's military technical runs its own fast circuit and never
  // touches the queue or service-lane state.
  updateMilitaryPatrol(loop, dt);

  return rageEvent;
}

function updateQueueLine(line, dt, route, player, queueGuard, panic = 1) {
  const playerGuard = queueGuard?.line === line ? queueGuard : null;
  const playerHalfLength = (player?.config?.chassis?.[2] ?? 4.4) * 0.5;
  let aheadS = null;

  for (const car of line.cars) {
    car._queueYield = false;

    // A car making an angry run keeps its logical place at the front of the
    // line, but its visual/collider transform is driven by updateRageRuns().
    if (car.raging) {
      aheadS = car.queueS;
      continue;
    }

    let maxS = aheadS === null ? line.lineEnd : aheadS - line.targetGap;

    if (playerGuard && car.queueS < playerGuard.s) {
      const carHalfLength = carDimensions(car.type).length * 0.5 * (car.scale ?? 1);
      const holdS = playerGuard.s - playerHalfLength - carHalfLength - QUEUE_PLAYER_BUFFER;
      maxS = Math.min(maxS, holdS);
      car._queueYield = true;
    }

    // If the player stopped in a slot that a scripted car already occupied,
    // pull that car back to the safe following distance instead of letting its
    // kinematic collider push the truck down the road.
    if (playerGuard && car.queueS > maxS) car.queueS = maxS;

    const gap = maxS - car.queueS;
    if (gap > 0.002) {
      const followSpeed = car.joining
        ? Math.min(LOOP_TOP_SPEED * panic, gap * 0.9 * panic)
        : Math.min(
          QUEUE_CREEP_SPEED * panic,
          Math.max(0.5, gap * QUEUE_FOLLOW_RATE * panic),
        );
      const step = Math.min(gap, followSpeed * dt);
      car.queueS += step;
      if (car.joining && gap < 0.5) car.joining = false;
    }

    if (car.visualScale < 1) {
      car.visualScale = Math.min(1, car.visualScale + dt * 1.2);
    }

    const frame = route.offsetFrame(car.queueS, car.queueLateral);
    car.x = frame.position.x;
    car.z = frame.position.z;
    car.y = 0.02;
    car.yaw = frame.yaw + car.yawOffset;

    if (car.lunge) {
      if (car._queueYield) {
        car.lunge = null;
        car.angryTimer = Math.max(car.angryTimer ?? 0, 1.2);
      } else {
        updateLunge(car, dt, player);
      }
    }

    aheadS = car.queueS;
  }
}

function worldToStation(loop, x, z) {
  const dx = x - loop.frame.position.x;
  const dz = z - loop.frame.position.z;
  return {
    x: dx * loop.frame.right.x + dz * loop.frame.right.z,
    z: -(dx * loop.frame.tangent.x + dz * loop.frame.tangent.z),
  };
}

function stationToWorld(loop, x, z) {
  return loop.frame.position
    .clone()
    .addScaledVector(loop.frame.right, x)
    .addScaledVector(loop.frame.tangent, -z);
}

function distance2D(ax, az, bx, bz) {
  return Math.hypot(ax - bx, az - bz);
}

/**
 * Nearest point on the road centreline. The queue runs along the route, and
 * using the station's local axes hundreds of metres away would be misleading
 * wherever the road curves. A coarse sample scan followed by a short
 * refinement is cheap enough to run per station.
 */
function projectOnRoute(route, x, z, hintS, range = 330) {
  const step = route.step || 2;
  const samples = route.samples;
  if (!samples?.length) {
    const frame = route.frameAt(hintS);
    return {
      s: hintS,
      lateral: (x - frame.position.x) * frame.right.x + (z - frame.position.z) * frame.right.z,
    };
  }

  const startIndex = Math.max(0, Math.floor((hintS - range) / step));
  const endIndex = Math.min(samples.length - 1, Math.ceil((hintS + range) / step));
  let bestIndex = Math.max(0, Math.min(samples.length - 1, Math.round(hintS / step)));
  let bestDistanceSq = Infinity;

  for (let i = startIndex; i <= endIndex; i += 1) {
    const sample = samples[i];
    const dx = x - sample.position.x;
    const dz = z - sample.position.z;
    const distanceSq = dx * dx + dz * dz;
    if (distanceSq < bestDistanceSq) {
      bestDistanceSq = distanceSq;
      bestIndex = i;
    }
  }

  const coarseS = samples[bestIndex].s;
  let bestS = coarseS;
  for (let s = Math.max(0, coarseS - step); s <= Math.min(route.length, coarseS + step); s += 0.25) {
    const frame = route.frameAt(s);
    const dx = x - frame.position.x;
    const dz = z - frame.position.z;
    const distanceSq = dx * dx + dz * dz;
    if (distanceSq < bestDistanceSq) {
      bestDistanceSq = distanceSq;
      bestS = s;
    }
  }

  const frame = route.frameAt(bestS);
  return {
    s: bestS,
    lateral: (x - frame.position.x) * frame.right.x + (z - frame.position.z) * frame.right.z,
  };
}

function makeQueueRageEvent(loop) {
  return {
    type: 'queue-rage',
    stationId: loop.station.id,
    stationName: loop.station.nameEnglish,
    ragingCars: STATION_RAGE_CARS,
    angryCars: STATION_ANGRY_CROWD,
  };
}

/**
 * The player can drive the inner northbound lane past the line without any
 * trouble. Entering the queue lane, passing on the east shoulder, or pulling
 * into a forecourt while the line is active is a queue jump.
 *
 * A player who joins behind the painted back of a queue file and follows it to
 * the station, or waits in it long enough for a place, earns a clean arrival.
 * Everybody else who reaches the forecourt is treated as a queue-jumper for
 * the whole stay.
 */
function checkQueueRage(loop, player, route, dt) {
  const position = player?.mesh?.position;
  if (!position) {
    loop.playerInRageZone = false;
    loop.queueFollowing = false;
    loop.queueFollowMaxS = -Infinity;
    loop.queueGuard = null;
    return null;
  }

  const local = worldToStation(loop, position.x, position.z);
  const stationDx = position.x - loop.frame.position.x;
  const stationDz = position.z - loop.frame.position.z;
  const nearStation = stationDx * stationDx + stationDz * stationDz < 430 * 430;
  const projected = nearStation
    ? projectOnRoute(route, position.x, position.z, loop.station.s - 150, 345)
    : { s: -Infinity, lateral: 0 };
  const lineStart = loop.station.s - 300;
  const lineEnd = loop.station.s - 34;
  const inQueueBand =
    nearStation &&
    projected.s > loop.station.s - 365 &&
    projected.s < loop.station.s - 16 &&
    projected.lateral > 5.55 &&
    projected.lateral < 16.5;
  const inQueueFile = inQueueBand && projected.lateral < 11.6;
  const onShoulder = inQueueBand && projected.lateral >= 11.6;
  const inForecourt =
    local.z > -34 && local.z < 34 && local.x > 13.0;
  const inStationArea =
    local.z > -40 && local.z < 40 && local.x > 11.5;

  loop.queueGuard = null;

  // Queue-following credit. You have to join behind the back of the painted
  // line and stay with it until the forecourt; cutting into the middle or
  // jumping out to the shoulder breaks the credit.
  if (inForecourt) {
    const finishedTheLine =
      loop.queueFollowing &&
      loop.queueFollowMaxS >= lineEnd - QUEUE_LEGIT_FRONT_REACH;
    if ((finishedTheLine || loop.queueCreditEarned) && !loop.queueLegitimate) {
      loop.queueLegitimate = true;
      loop.stationRageActive = false;
      releaseStationRage(loop, route);
    }
    loop.queueFollowing = false;
    loop.queueFollowMaxS = -Infinity;
    loop.queueFollowLost = 0;
    loop.queueWaitTime = 0;
  } else if (inQueueFile) {
    if (!loop.queueFollowing && projected.s <= lineStart + QUEUE_JOIN_MARGIN) {
      loop.queueFollowing = true;
      loop.queueFollowMaxS = projected.s;
      loop.queueFollowLost = 0;
      loop.queueWaitTime = 0;
    }
    if (loop.queueFollowing) {
      loop.queueFollowLost = 0;
      loop.queueFollowMaxS = Math.max(loop.queueFollowMaxS, projected.s);
      if (Math.abs(player?.speed ?? 0) < QUEUE_WAIT_MAX_SPEED) {
        loop.queueWaitTime += dt;
        if (loop.queueWaitTime >= QUEUE_WAIT_CREDIT) {
          loop.queueCreditEarned = true;
        }
      }
    }
  } else if (loop.queueFollowing) {
    loop.queueFollowLost += dt;
    if (loop.queueFollowLost >= QUEUE_FOLLOW_GRACE) {
      loop.queueFollowing = false;
      loop.queueFollowMaxS = -Infinity;
      loop.queueFollowLost = 0;
      loop.queueWaitTime = 0;
    }
  }

  if (
    !inForecourt &&
    (projected.s < loop.station.s - 380 || projected.s > loop.station.s + 35)
  ) {
    loop.queueFollowing = false;
    loop.queueFollowMaxS = -Infinity;
    loop.queueFollowLost = 0;
    loop.queueWaitTime = 0;
    loop.queueCreditEarned = false;
  }

  // Select the file the player is actually following. The two painted files
  // are only a couple of metres apart, so use the nearest one rather than
  // holding both and creating an artificial hole in the other line.
  if (loop.queueFollowing && inQueueFile && !inForecourt) {
    let bestLine = null;
    let bestOffset = Infinity;
    for (const line of loop.lines) {
      const offset = Math.abs(projected.lateral - line.offset);
      if (offset < bestOffset) {
        bestOffset = offset;
        bestLine = line;
      }
    }
    if (bestLine && bestOffset <= 2.4) {
      loop.queueGuard = { line: bestLine, s: projected.s };
    }
  }

  let event = null;

  // Entering the forecourt without queue credit starts a station-wide anger
  // that lasts until the player leaves. Shoulder incidents also announce the
  // rage, so crossing from the shoulder into the forecourt does not repeat the
  // warning immediately.
  if (inForecourt) {
    loop.stationOutside = 0;
    if (!loop.queueLegitimate && !loop.stationRageActive) {
      loop.stationRageActive = true;
      loop.stationSiegeStarted = false;
      loop.stationChaseCars = [];
      loop.stationHonkCooldown = nextStationHonkDelay();
      if (!loop.stationRageAnnounced) {
        loop.stationRageAnnounced = true;
        event = makeQueueRageEvent(loop);
      }
    } else if (loop.queueLegitimate) {
      loop.stationRageActive = false;
    }
  } else if (inStationArea) {
    loop.stationOutside = 0;
  } else if (
    loop.stationRageActive ||
    loop.queueLegitimate ||
    loop.stationRageAnnounced
  ) {
    const hadStationVisit = loop.stationRageActive || loop.queueLegitimate;
    loop.stationOutside += dt;
    if (loop.stationOutside >= STATION_LEAVE_GRACE) {
      loop.stationRageActive = false;
      releaseStationRage(loop, route);
      loop.stationRageAnnounced = false;
      if (hadStationVisit) {
        loop.queueLegitimate = false;
        loop.queueWaitTime = 0;
        loop.queueCreditEarned = false;
      }
      loop.stationOutside = 0;
    }
  }

  const followingQueue =
    loop.queueFollowing || loop.queueLegitimate || loop.queueCreditEarned;
  const alongsideQueue = (inQueueFile || onShoulder) && !followingQueue;

  if (alongsideQueue) {
    const enteredZone = !loop.playerInRageZone;
    loop.playerInRageZone = true;

    if (
      enteredZone &&
      loop.rageCooldown <= 0 &&
      loop.lines.some((line) => line.cars.length > 6)
    ) {
      loop.rageCooldown = RAGE_COOLDOWN;
      loop.rageWindow = RAGE_WINDOW;
      provokeQueue(loop, player, local);
      if (!event && !loop.stationRageAnnounced) {
        event = makeQueueRageEvent(loop);
      }
      loop.stationRageAnnounced = true;
    }
  } else {
    loop.playerInRageZone = false;
  }

  return event;
}

function releaseStationRage(loop, route) {
  const candidates = [...loop.stationChaseCars];
  for (const line of loop.lines) {
    for (const car of line.cars) {
      if (car.rage?.siege || car.rage?.stationChase) candidates.push(car);
    }
  }

  const released = new Set();
  for (const car of candidates) {
    if (released.has(car) || !car.rage) continue;
    released.add(car);
    car.rage.siege = false;
    car.rage.stationChase = false;
    beginRageReturn(car, route);
  }

  loop.stationChaseCars = [];
  loop.stationSiegeStarted = false;
}

function beginStationSiege(loop, player) {
  const playerPosition = player?.mesh?.position;
  if (!playerPosition) return;

  const chasers = [];
  // Adopt any drivers already chasing from the shoulder incident, then fill
  // the group up to four. After this point the same cars stay on the attack.
  for (const line of loop.lines) {
    for (const car of line.cars) {
      if (chasers.length >= STATION_RAGE_CARS) break;
      if (car.raging && car.rage) chasers.push(car);
    }
  }

  while (chasers.length < STATION_RAGE_CARS) {
    const choices = [];
    for (const line of loop.lines) {
      let active = 0;
      for (const car of line.cars) {
        if (chasers.includes(car)) active += 1;
      }
      const candidate = line.cars.find(
        (car) => !car.raging && !car.rage && !chasers.includes(car),
      );
      if (candidate) choices.push({ active, candidate });
    }
    choices.sort(
      (a, b) => a.active - b.active || b.candidate.queueS - a.candidate.queueS,
    );
    const candidate = choices[0]?.candidate;
    if (!candidate) break;
    startRageRun(loop, candidate, player, { station: true, slot: chasers.length });
    chasers.push(candidate);
  }

  for (let slot = 0; slot < chasers.length; slot += 1) {
    const car = chasers[slot];
    const rage = car.rage;
    if (!rage) continue;

    car.raging = true;
    car.angry = true;
    car.angryTimer = 0;
    car.lunge = null;
    rage.stationChase = true;
    rage.siege = true;
    rage.slot = slot;
    rage.approach = STATION_APPROACH_OFFSETS[slot % STATION_APPROACH_OFFSETS.length];
    rage.bumpStrength = STATION_BUMP_STRENGTH;
    rage.attackCooldown = 0;

    const carLocal = worldToStation(loop, car.x, car.z);
    const carOutside =
      carLocal.x < 13.2 || carLocal.z < -33.5 || carLocal.z > 33.5;
    beginSiegeAttack(loop, car, player, { withVia: carOutside });
  }

  loop.stationChaseCars = chasers;
  loop.stationSiegeStarted = true;
}

function markAngryCrowd(loop, playerPosition, count) {
  if (!playerPosition) return;
  const nearby = [];
  for (const line of loop.lines) {
    for (const car of line.cars) {
      const distance = distance2D(car.x, car.z, playerPosition.x, playerPosition.z);
      if (distance < 90) nearby.push({ car, distance });
    }
  }
  nearby.sort((a, b) => a.distance - b.distance);
  for (const { car } of nearby.slice(0, count)) {
    car.angry = true;
    if (!car.raging) {
      car.angryTimer = Math.max(car.angryTimer ?? 0, 1.6);
    }
  }
}

function updateStationRage(loop, dt, player) {
  if (!loop.stationRageActive) return;
  const playerPosition = player?.mesh?.position;
  if (!playerPosition) return;

  // The whole front of both lines joins in: a dozen-plus cars flash their
  // hazards while the fixed group of four keeps attacking.
  markAngryCrowd(loop, playerPosition, STATION_ANGRY_CROWD);

  loop.stationHonkCooldown = Math.max(0, loop.stationHonkCooldown - dt);
  if (loop.stationHonkCooldown <= 0) {
    loop.honkPulse += 1;
    loop.stationHonkCooldown = nextStationHonkDelay();
  }

  if (!loop.stationSiegeStarted) beginStationSiege(loop, player);
}

function provokeQueue(loop, player, local) {
  if (loop.stationRageActive) return;
  const playerPosition = player.mesh.position;
  markAngryCrowd(loop, playerPosition, STATION_ANGRY_CROWD);
  const frontCars = loop.lines
    .map((line) => line.cars[0])
    .filter((car) => car && !car.raging);
  const chaseCars = [];

  if (local.z < 60) {
    const nearest = [];
    for (const line of loop.lines) {
      for (const car of line.cars.filter((entry) => !entry.raging).slice(0, 2)) {
        const distance = distance2D(car.x, car.z, playerPosition.x, playerPosition.z);
        if (distance < 58) nearest.push({ car, distance });
      }
    }
    nearest.sort((a, b) => a.distance - b.distance);
    for (const [index, { car }] of nearest.slice(0, 3).entries()) {
      startRageRun(loop, car, player, {
        approach: STATION_APPROACH_OFFSETS[index % STATION_APPROACH_OFFSETS.length],
        bumpStrength: 0.9,
      });
      chaseCars.push(car);
    }
  }

  // The other front drivers flash their hazards and edge at the line even if
  // they cannot leave their place without blocking the whole queue.
  if (local.z < 70) {
    for (const car of frontCars) {
      if (chaseCars.includes(car)) continue;
      car.angry = true;
      car.angryTimer = Math.max(car.angryTimer ?? 0, 4);
    }
  }

  // Cars close enough to the player swerve and shoulder-check.
  const close = [];
  for (const line of loop.lines) {
    for (const car of line.cars) {
      if (car.raging || car.lunge || car.angerCooldown > 0) continue;
      const distance = distance2D(car.x, car.z, playerPosition.x, playerPosition.z);
      if (distance < 17) close.push({ car, distance });
    }
  }
  close.sort((a, b) => a.distance - b.distance);
  for (const { car } of close.slice(0, 5)) startLunge(car);
}

function provokeNearbyQueue(loop, player) {
  if (loop.stationRageActive) return;
  const playerPosition = player.mesh.position;
  const close = [];
  for (const line of loop.lines) {
    for (const car of line.cars) {
      if (car.raging || car.lunge || car.angerCooldown > 0) continue;
      const distance = distance2D(car.x, car.z, playerPosition.x, playerPosition.z);
      if (distance < RAGE_LOCAL_RADIUS) close.push({ car, distance });
    }
  }
  close.sort((a, b) => a.distance - b.distance);
  for (const { car } of close.slice(0, 4)) startLunge(car);
}

function startLunge(car) {
  if (car.raging || car.lunge || car.angerCooldown > 0) return;
  car.lunge = {
    elapsed: 0,
    bumped: false,
  };
  car.angry = true;
  car.angryTimer = 0;
  car.angerCooldown = 8;
}

function updateLunge(car, dt, player) {
  const lunge = car.lunge;
  lunge.elapsed += dt;

  if (lunge.elapsed >= RAGE_LUNGE_DURATION) {
    car.lunge = null;
    car.angryTimer = 2.2;
    return;
  }

  const position = player?.mesh?.position;
  if (!position) return;

  const dx = position.x - car.x;
  const dz = position.z - car.z;
  const distance = Math.hypot(dx, dz);
  if (distance < 1e-3) return;

  const progress = lunge.elapsed / RAGE_LUNGE_DURATION;
  const envelope = Math.sin(progress * Math.PI);
  const reach = Math.min(1.7, Math.max(0, distance - 1.9));

  car.x += (dx / distance) * reach * envelope;
  car.z += (dz / distance) * reach * envelope;
  car.yaw = dampAngle(car.yaw, Math.atan2(-dx, -dz), 5, dt);

  const contactDistance = Math.hypot(position.x - car.x, position.z - car.z);
  if (!lunge.bumped && progress > 0.2 && progress < 0.82 && contactDistance < 2.65) {
    bumpPlayer(player, car, 0.7);
    lunge.bumped = true;
  }
}

function stationApproachTarget(loop, playerPosition, offset) {
  const local = worldToStation(loop, playerPosition.x, playerPosition.z);
  // Keep the full attack radius even at the forecourt edges. Clamping the
  // target would push it inside the player's collider and let Ammo resolve the
  // overlap with an explosive impulse.
  return stationToWorld(loop, local.x + offset.x, local.z + offset.z);
}

function offsetPlayerTarget(loop, playerPosition, offset) {
  return {
    x: playerPosition.x + loop.frame.right.x * offset.x - loop.frame.tangent.x * offset.z,
    z: playerPosition.z + loop.frame.right.z * offset.x - loop.frame.tangent.z * offset.z,
  };
}

function startRageRun(loop, car, player, options = {}) {
  if (car.rage || car.raging) return;

  const position = player.mesh.position;
  const local = worldToStation(loop, position.x, position.z);
  const waypoints = [];
  const stationChase = options.station === true;
  const slot = Number.isInteger(options.slot) ? options.slot : null;
  const approach = stationChase && slot !== null
    ? STATION_APPROACH_OFFSETS[slot % STATION_APPROACH_OFFSETS.length]
    : options.approach ?? null;

  if (local.x > 13 && local.z > -34 && local.z < 34) {
    const nearZ = local.z >= 0 ? 18 : -18;
    // Split a multi-car attack between both forecourt driveways.
    const viaZ = stationChase && slot !== null && slot % 2 === 1 ? -nearZ : nearZ;
    waypoints.push(stationToWorld(loop, 13.2, viaZ));
  }

  if (approach) {
    waypoints.push(
      stationChase
        ? stationApproachTarget(loop, position, approach)
        : offsetPlayerTarget(loop, position, approach),
    );
  } else {
    waypoints.push({ x: position.x, z: position.z });
  }

  car.raging = true;
  car.angry = true;
  car.angryTimer = 0;
  car.angerCooldown = 8;
  car.lunge = null;
  car.rage = {
    phase: 'out',
    elapsed: 0,
    hold: 0,
    waypoints,
    index: 0,
    bumped: false,
    via: waypoints.length > 1 ? waypoints[0] : null,
    stationChase,
    slot,
    approach,
    bumpStrength: options.bumpStrength ?? (stationChase ? STATION_BUMP_STRENGTH : 2.0),
    siege: false,
    attackCooldown: 0,
    recoverTarget: null,
    recoverTime: 0,
  };
}

function beginSiegeAttack(loop, car, player, { withVia = false } = {}) {
  const rage = car.rage;
  if (!rage || !rage.siege || !loop.stationRageActive) return;
  const playerPosition = player?.mesh?.position;
  if (!playerPosition) return;

  const offset = rage.approach ?? STATION_APPROACH_OFFSETS[0];
  const waypoints = [];
  if (withVia) {
    const local = worldToStation(loop, playerPosition.x, playerPosition.z);
    const nearZ = local.z >= 0 ? 18 : -18;
    const viaZ = (rage.slot ?? 0) % 2 === 1 ? -nearZ : nearZ;
    waypoints.push(stationToWorld(loop, 13.2, viaZ));
  }
  waypoints.push(stationApproachTarget(loop, playerPosition, offset));

  rage.waypoints = waypoints;
  rage.index = 0;
  rage.phase = 'out';
  rage.elapsed = 0;
  rage.hold = 0;
  rage.bumped = false;
  rage.recoverTarget = null;
  rage.recoverTime = 0;
  rage.via = waypoints.length > 1 ? waypoints[0] : null;
  car.angry = true;
  car.angryTimer = 0;
}

function stationRecoverTarget(loop, playerPosition, car, rage) {
  const playerLocal = worldToStation(loop, playerPosition.x, playerPosition.z);
  const carLocal = worldToStation(loop, car.x, car.z);
  let dx = carLocal.x - playerLocal.x;
  let dz = carLocal.z - playerLocal.z;
  let length = Math.hypot(dx, dz);
  if (length < 1e-3) {
    const fallback = rage.approach ?? STATION_APPROACH_OFFSETS[0];
    dx = fallback.x;
    dz = fallback.z;
    length = Math.hypot(dx, dz) || 1;
  }

  // Retreat and re-attack along the same radial line. Crossing the middle of
  // the circle to reach a fixed slot would drive the car through the Hilux.
  const nx = dx / length;
  const nz = dz / length;
  rage.approach = {
    x: nx * STATION_ATTACK_RADIUS,
    z: nz * STATION_ATTACK_RADIUS,
  };
  return stationToWorld(
    loop,
    playerLocal.x + nx * STATION_RECOVER_DISTANCE,
    playerLocal.z + nz * STATION_RECOVER_DISTANCE,
  );
}

function startSiegeRecover(loop, car, playerPosition) {
  const rage = car.rage;
  if (!rage || !rage.siege) return;
  rage.phase = 'recover';
  rage.recoverTime = STATION_RECOVER_TIME;
  rage.attackCooldown = STATION_ATTACK_COOLDOWN;
  rage.bumped = true;
  rage.recoverTarget = playerPosition
    ? stationRecoverTarget(loop, playerPosition, car, rage)
    : null;
}

function updateRageRuns(loop, dt, route, player) {
  for (const line of loop.lines) {
    for (const car of line.cars) {
      if (car.rage) updateRageCar(loop, car, dt, route, player);
    }
  }
}

function updateRageCar(loop, car, dt, route, player) {
  const rage = car.rage;
  const playerPosition = player?.mesh?.position;
  rage.attackCooldown = Math.max(0, (rage.attackCooldown ?? 0) - dt);

  if (rage.siege && !loop.stationRageActive) {
    rage.siege = false;
    beginRageReturn(car, route);
    return;
  }

  if (rage.phase === 'out') {
    rage.elapsed += dt;
    const timeout = rage.siege ? RAGE_SIEGE_TIMEOUT : RAGE_RUN_TIMEOUT;

    if (rage.elapsed > timeout) {
      if (rage.siege) startSiegeRecover(loop, car, playerPosition);
      else beginRageReturn(car, route);
      return;
    }

    // Keep tracking the player while the chase is in the station apron, so a
    // car follows the Hilux around the forecourt instead of ramming an empty
    // patch of tarmac.
    if (playerPosition && rage.waypoints.length > 0) {
      const local = worldToStation(loop, playerPosition.x, playerPosition.z);
      if (local.x > 8 && local.x < 52 && local.z > -40 && local.z < 40) {
        const finalTarget = rage.waypoints[rage.waypoints.length - 1];
        if (rage.approach) {
          const target = rage.stationChase
            ? stationApproachTarget(loop, playerPosition, rage.approach)
            : offsetPlayerTarget(loop, playerPosition, rage.approach);
          finalTarget.x = target.x;
          finalTarget.z = target.z;
        } else {
          finalTarget.x = playerPosition.x;
          finalTarget.z = playerPosition.z;
        }
      }
    }

    const target = rage.waypoints[rage.index];
    if (!target) {
      if (rage.siege) beginSiegeAttack(loop, car, player, { withVia: false });
      else beginRageReturn(car, route);
      return;
    }

    moveRageCar(car, target, RAGE_RUN_SPEED, dt);

    const distance = distance2D(car.x, car.z, target.x, target.z);
    if (
      rage.waypoints.length > 1 &&
      rage.index < rage.waypoints.length - 1 &&
      distance < 1.8
    ) {
      rage.index += 1;
    }

    if (
      playerPosition &&
      rage.attackCooldown <= 0 &&
      distance2D(car.x, car.z, playerPosition.x, playerPosition.z) < RAGE_BUMP_DISTANCE
    ) {
      if (!rage.bumped) bumpPlayer(player, car, rage.bumpStrength ?? 2.0);
      rage.bumped = true;
      if (rage.siege) {
        startSiegeRecover(loop, car, playerPosition);
      } else {
        rage.phase = 'hold';
        rage.hold = 0.7;
      }
      return;
    }

    if (
      !rage.siege &&
      rage.index >= rage.waypoints.length - 1 &&
      distance < 1.8
    ) {
      rage.phase = 'hold';
      rage.hold = 0.45;
    }
  } else if (rage.phase === 'recover') {
    rage.recoverTime -= dt;
    if (rage.recoverTarget) {
      moveRageCar(car, rage.recoverTarget, RAGE_RUN_SPEED * 0.8, dt);
    }
    const distance = rage.recoverTarget
      ? distance2D(car.x, car.z, rage.recoverTarget.x, rage.recoverTarget.z)
      : 0;

    if (rage.recoverTime <= 0 || (rage.recoverTarget && distance < 0.8)) {
      if (!loop.stationRageActive) {
        rage.siege = false;
        beginRageReturn(car, route);
      } else {
        beginSiegeAttack(loop, car, player, { withVia: false });
      }
    }
  } else if (rage.phase === 'hold') {
    rage.hold -= dt;
    if (rage.hold <= 0) {
      if (rage.siege) beginSiegeAttack(loop, car, player, { withVia: false });
      else beginRageReturn(car, route);
    }
  } else {
    const baseFrame = route.offsetFrame(car.queueS, car.queueLateral);
    const base = baseFrame.position;
    const target = rage.returnWaypoints[rage.index];

    if (!target) {
      car.x = base.x;
      car.z = base.z;
      car.y = 0.02;
      car.yaw = baseFrame.yaw;
      car.raging = false;
      car.angry = false;
      car.rage = null;
      return;
    }

    moveRageCar(car, target, RAGE_RETURN_SPEED, dt);

    const distance = distance2D(car.x, car.z, target.x, target.z);
    const isLast = rage.index >= rage.returnWaypoints.length - 1;
    if ((isLast && distance < 0.35) || (!isLast && distance < 1.2)) {
      rage.index += 1;
    }
  }
}

function beginRageReturn(car, route) {
  const rage = car.rage;
  if (!rage || rage.phase === 'back') return;

  const baseFrame = route.offsetFrame(car.queueS, car.queueLateral);
  // Only reuse the driveway waypoint if the chase already passed it. A car
  // that bumped the player on the forecourt apron should back straight out to
  // its place instead of driving forward through the truck a second time.
  const reuseVia = rage.via && rage.index > 0;
  rage.returnWaypoints = reuseVia
    ? [{ x: rage.via.x, z: rage.via.z }, { x: baseFrame.position.x, z: baseFrame.position.z }]
    : [{ x: baseFrame.position.x, z: baseFrame.position.z }];
  rage.index = 0;
  rage.phase = 'back';
}

function moveRageCar(car, target, speed, dt) {
  const dx = target.x - car.x;
  const dz = target.z - car.z;
  const distance = Math.hypot(dx, dz);
  if (distance < 1e-4) return;

  const step = Math.min(distance, speed * dt);
  car.x += (dx / distance) * step;
  car.z += (dz / distance) * step;
  car.y = 0.02;
  car.yaw = dampAngle(car.yaw, Math.atan2(-dx, -dz), 7, dt);
}

function bumpPlayer(player, car, strength) {
  const body = player?.body;
  if (!body) return;

  const position = player.mesh.position;
  let dx = position.x - car.x;
  let dz = position.z - car.z;
  const length = Math.hypot(dx, dz);
  if (length < 1e-4) {
    dx = 1;
    dz = 0;
  } else {
    dx /= length;
    dz /= length;
  }

  const impulse = PLAYER_BUMP_IMPULSE * strength;
  body.applyCentralImpulse({ x: dx * impulse, y: 35 * strength, z: dz * impulse });
  player.collideWithCar?.(car, { source: 'rage', strength });
}

function updateAngryTimers(loop, dt) {
  for (const line of loop.lines) {
    for (const car of line.cars) {
      if (car.angerCooldown > 0) {
        car.angerCooldown = Math.max(0, car.angerCooldown - dt);
      }
      if (car.angryTimer <= 0 || car.raging || car.lunge) continue;
      car.angryTimer -= dt;
      if (car.angryTimer <= 0) {
        car.angryTimer = 0;
        car.angry = false;
      }
    }
  }
}

function pumpTimeFor(car) {
  if (car.type === 'raksha') {
    return Math.max(2.5, (1 - car.fuel) / car.rakshaConfig.refuelRate);
  }
  return AI_PUMP_TIME_MIN + (car.index % AI_PUMP_TIME_SPREAD);
}

function updateServiceLane(loop, line, dt, panic = 1) {
  const lane = line.lane;
  const { frame } = loop;

  for (let slotIndex = 0; slotIndex < lane.slots.length; slotIndex += 1) {
    const car = lane.slots[slotIndex].car;
    if (!car) continue;

    if (car.state === 'shifting') {
      const targetZ = lane.slots[slotIndex].z;
      const delta = targetZ - car.localZ;
      const step = Math.sign(delta) * Math.min(
        Math.abs(delta),
        SERVICE_SHIFT_SPEED * panic * dt,
      );
      car.localZ += step;
      if (Math.abs(targetZ - car.localZ) < 0.01) {
        car.localZ = targetZ;
        car.state = 'service';
        if (slotIndex === 0) {
          car.fuelRemaining = pumpTimeFor(car);
        }
      }
      placeInLane(car, lane, loop);
    } else if (car.state === 'service') {
      placeInLane(car, lane, loop);
    }
  }

  const front = lane.slots[0].car;
  if (!front || front.state !== 'service') return;

  if (front.fuelRemaining == null || !Number.isFinite(front.fuelRemaining)) {
    front.fuelRemaining = pumpTimeFor(front);
  }

  front.fuelRemaining -= dt;
  if (front.type === 'raksha') {
    front.fuel = Math.min(1, front.fuel + front.rakshaConfig.refuelRate * dt);
    if (front.fuel >= 1) front.fuelRemaining = Math.min(front.fuelRemaining, 0.6);
  }
  if (front.fuelRemaining > 0 || loop.exitBusy) return;

  startExit(loop, line, front);
}

function placeInLane(car, lane, loop) {
  const point = loop.frame.position
    .clone()
    .addScaledVector(loop.frame.right, lane.x)
    .addScaledVector(loop.frame.tangent, -car.localZ);
  car.x = point.x;
  car.z = point.z;
  car.y = 0.02;
  car.yaw = loop.frame.yaw;
}

function startExit(loop, line, car) {
  const lane = line.lane;
  lane.slots[0].car = null;
  car.state = 'exiting';
  car.exitDistance = 0;
  car.exitSpeed = 3.2;
  car.fuelRemaining = null;
  car.hazardOn = false;
  loop.exitBusy = true;
  car.exitCurve = lane.exitCurve;
  car.exitLength = lane.exitLength;
  car.speedProfile = lane.speedProfile;
  loop.exiting.push(car);

  // The two cars behind move up one pump position.
  const middle = lane.slots[1].car;
  const back = lane.slots[2].car;
  lane.slots[0].car = middle;
  if (middle) {
    middle.slotIndex = 0;
    middle.state = 'shifting';
    middle.fuelRemaining = null;
  }
  lane.slots[1].car = back;
  if (back) {
    back.slotIndex = 1;
    back.state = 'shifting';
    back.fuelRemaining = null;
  }
  lane.slots[2].car = null;
}

function startEntry(loop, line, front) {
  line.cars.shift();
  front.state = 'entering';
  front.lunge = null;
  front.angry = false;
  front.angryTimer = 0;
  front.entryElapsed = 0;
  front.entryDuration = Math.max(
    ENTRY_MIN_DURATION,
    line.lane.entryStop / ENTRY_CRUISE_SPEED,
  );
  front.entryCurve = line.lane.entryCurve;
  front.entryStop = line.lane.entryStop;
  front.serviceLane = line.lane;
  front.slotIndex = 2;
  front.localZ = 10;
  front.fuelRemaining = null;
  front.hazardOn = false;
  line.lane.slots[2].car = front;
  loop.entryBusy = front;
}

function updateEntryCar(loop, dt) {
  const car = loop.entryBusy;
  if (!car) return;

  car.entryElapsed += dt;
  const u = Math.min(1, car.entryElapsed / car.entryDuration);
  const eased = u * u * (3 - 2 * u);
  const point = car.entryCurve.getPointAt((car.entryStop / car.entryCurve.getLength()) * eased);
  const tangent = car.entryCurve.getTangentAt((car.entryStop / car.entryCurve.getLength()) * eased);
  car.x = point.x;
  car.z = point.z;
  car.y = 0.02;
  car.yaw = Math.atan2(-tangent.x, -tangent.z);

  if (u < 1) return;

  car.state = 'service';
  car.slotIndex = 2;
  car.localZ = 10;
  placeInLane(car, car.serviceLane, loop);
  loop.entryBusy = null;
}

function updateExiting(loop, dt, panic = 1, route = null, hitchhikers = null) {
  for (let i = loop.exiting.length - 1; i >= 0; i -= 1) {
    const car = loop.exiting[i];
    const line = loop.lines[car.homeLine];
    const length = car.exitLength;
    const progress = Math.min(1, car.exitDistance / length);
    const raksha = car.type === 'raksha';
    const rideControl = raksha
      ? hitchhikers?.beforeRideMove(car, dt)
      : null;
    let target = sampleSpeedProfile(car.speedProfile, progress) * panic;
    if (raksha) {
      target *= car.rakshaConfig.speedMultiplier * (rideControl?.speedScale ?? 1);
      if (car.fuel < car.rakshaConfig.lowFuelThreshold) target *= 0.75;
      if (car.fuel <= 0) target = Math.min(target, 1.2); // empty-tank limp to the pumps
    }
    if (rideControl?.hold) target = 0;

    // If the queue is backed up to the merge, wait seven metres short of the
    // hand-off point instead of driving into the last queued car.
    const last = line.cars[line.cars.length - 1];
    const queueGap = last ? last.queueS - loop.joinS : Infinity;
    const canJoin = queueGap >= line.targetGap;
    let cap = canJoin ? length : Math.max(0, length - LOOP_FOLLOW_GAP);

    // Same-file departures share exactly the same curve, so their path
    // distance is a reliable following gap. Rakshas leave a slightly shorter
    // gap on the open road, but use the normal safe merge gap at the queue.
    let aheadDistance = Infinity;
    for (const other of loop.exiting) {
      if (
        other !== car
        && other.homeLine === car.homeLine
        && other.exitDistance > car.exitDistance
      ) {
        aheadDistance = Math.min(aheadDistance, other.exitDistance);
      }
    }
    if (aheadDistance !== Infinity) {
      const followGap = raksha ? car.rakshaConfig.followGap : LOOP_FOLLOW_GAP;
      cap = Math.min(cap, aheadDistance - followGap);
    }
    cap = Math.max(0, Math.min(length, cap));
    if (rideControl?.hold) cap = Math.min(cap, car.exitDistance);

    const acceleration = raksha ? 2.8 * car.rakshaConfig.accelerationMultiplier : 2.8;
    if (car.exitSpeed < target) {
      car.exitSpeed = Math.min(target, car.exitSpeed + acceleration * panic * dt);
    } else {
      car.exitSpeed = Math.max(target, car.exitSpeed - 4.2 * dt);
    }

    const previousDistance = car.exitDistance;
    const nextDistance = Math.min(cap, car.exitDistance + car.exitSpeed * dt);
    if (nextDistance <= car.exitDistance + 1e-5) {
      car.exitSpeed = 0;
    } else {
      car.exitDistance = nextDistance;
    }

    const u = Math.min(1, car.exitDistance / length);
    const point = car.exitCurve.getPointAt(u);
    const tangent = car.exitCurve.getTangentAt(u);
    car.x = point.x;
    car.z = point.z;
    car.y = 0.02;
    car.yaw = Math.atan2(-tangent.x, -tangent.z);

    if (car.state === 'exiting' && car.exitDistance > 70) {
      car.state = 'returning';
    }

    if (raksha) {
      car.rakshaLean = 0;
      if (car.state === 'returning' && !car.raksha.stop && car.exitSpeed > 3) {
        const phase = car.exitDistance * 0.055 + car.index * 1.7;
        const weave = Math.sin(phase) * car.rakshaConfig.weaveAmplitude;
        car.x += -tangent.z * weave;
        car.z += tangent.x * weave;
        car.yaw += Math.cos(phase) * 0.025;
        car.rakshaLean = Math.cos(phase) * 0.025;
      }
      hitchhikers?.afterRideMove(car, car.exitDistance - previousDistance);
    }

    if (u >= 1 && canJoin) {
      joinQueue(loop, car);
      loop.exiting.splice(i, 1);
    }
  }

  // Recompute the gate from the cars that are still clearing the forecourt.
  loop.exitBusy = loop.exiting.some((car) => car.exitDistance <= 42);
}

function joinQueue(loop, car) {
  const line = loop.lines[car.homeLine];
  car.state = 'queue';
  car.queueS = loop.joinS;
  car.queueLateral = line.offset;
  car.yawOffset = 0;
  car.joining = true;
  car.visible = true;
  car.visualScale = 1;
  car.hazardOn = car.hazard;
  car.slotIndex = null;
  car.localZ = null;
  car.fuelRemaining = null;
  car.exitDistance = 0;
  car.exitSpeed = 0;
  car.exitCurve = null;
  car.exitLength = 0;
  car.speedProfile = null;
  line.cars.push(car);
}

function pickType(roll) {
  let cumulative = 0;
  for (const [type, weight] of TYPE_WEIGHTS) {
    cumulative += weight;
    if (roll <= cumulative) return type;
  }
  return 'sedan';
}
